// Cart drawer opens are triggered by custom ATC paths via cart:update
// events that carry rendered `sections` inside `event.detail.data`.
// This lets cart-items-component morph drawer contents synchronously
// from the same /cart/add.js response that triggered the open,
// preventing the morph-mid-animation race that caused the half-open
// stuck-drawer bug.
//
// Hard rules — do not break:
// 1. `sections` MUST live inside `event.detail.data`, not as a
//    sibling. component-cart-items.js reads `detail.data.sections`.
// 2. Custom ATC paths must pass `sections: <comma-joined ids>` in
//    the /cart/add.js body so Shopify returns the rendered HTML.
// 3. Do NOT add setTimeout delays around cartDrawer.open() — they
//    re-introduce the morph race.
//
// See diagnosis reports 2026-04-19 and 2026-04-27 for full context.

import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    const { dialog } = this.refs;

    // Guard: if a closing animation is in flight (dialog-closing class added
    // by dialog.js:87 before awaiting onAnimationEnd at dialog.js:90), defer
    // the open until that animation finishes — otherwise showDialog() races
    // the close and leaves the drawer stuck half-open.
    //
    // Fallback timer covers prefers-reduced-motion: reduce, where the close
    // animation is suppressed entirely and animationend never fires. Close
    // animation is var(--animation-speed) = 125ms (theme-styles-variables.liquid);
    // 300ms gives a comfortable buffer without being perceptible.
    if (dialog?.classList.contains('dialog-closing')) {
      let opened = false;
      const openOnce = () => {
        if (opened) return;
        opened = true;
        this.showDialog();
      };

      dialog.addEventListener('animationend', openOnce, { once: true });
      setTimeout(openOnce, 300);
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
